import {
  ApplicationConfig, ErrorHandler,
  importProvidersFrom, inject,
  isDevMode, provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { provideState, provideStore } from '@ngrx/store';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { I18N_PROVIDERS } from '@core/i18n/i18n';
import { provideI18Next } from 'angular-i18next';
import { JwtModule } from '@auth0/angular-jwt';
import { getCookie } from '@core/helpers/cookie.helper';
import { TOKEN_NAME } from '@data/config';
import { ApiModule as BackendApiModule, Configuration } from '@api/backend';
import { environment } from '@environments/environment';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { InitStore } from '@app2/store/init/init.store';
import { ErrorInterceptor } from '@core/services/interceptor/error.interceptor';
import { JwtInterceptor } from '@core/services/interceptor/jwt.interceptor';
import { SessionEffects, sessionFeatureKey, sessionReducer } from '@store/session';
import { provideEffects } from '@ngrx/effects';
import { PornRepository } from '@core/repositories/porn.repository';
import { PornWebRepository } from '@data/repositories/porn-web.repository';
import { CategoryStore } from '@app2/store/category/category.store';
import { TagStore } from '@app2/store/tag/tag.store';
import { ToastrModule } from 'ngx-toastr';
import { FapsStore } from '@app2/store/faps/faps.store';
import { ViewStore } from '@app2/store/view/view.store';
import { AuthRepository, CommonRepository, FapRepository, MediaRepository, UserRepository } from '@core/repositories';
import {
  AuthWebRepository,
  CommonWebRepository,
  FapWebRepository,
  MediaWebRepository,
  PaymentWebRepository,
} from '@data/repositories';
import { VideoDirective } from '@app2/shared/directives/video.directive';
import { AdsStore } from '@app2/store/ads/ads.store';
import { AdsRepository } from '@core/repositories/ads.repository';
import { AdsWebRepository } from '@data/repositories/ads-web.repository';
import { SettingsEffects, settingsFeatureKey, settingsReducer } from '@store/settings';
import {
  initializeAnalytics,
  provideAnalytics,
  UserTrackingService,
} from '@angular/fire/analytics';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UserViewSelectorService } from '@private/user/components/user-view-selector/user-view-selector.service';
import { PaymentEffects, paymentFeatureKey, paymentReducer } from '@store/payment';
import {
  paymentFeatureKey as paymentFeatureKeyV2, paymentReducer as paymentReducerV2,
} from '@app2/store/payment/payment.reducer';
import { PaymentEffects as PaymentEffectsV2 } from '@app2/store/payment/payment.effects';
import { PaymentRepository } from '@core/repositories/payment.repository';
import { UserWebRepository } from '@data/repositories/user-web.repository';
import { InitEffects } from '@app2/store/init/init.effects';
import { FapsEffects } from '@app2/store/faps/faps.effects';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Material from '@primeng/themes/material';
import { PromoCodeEffects, promoCodeFeatureKey, promoCodeReducer } from '@store/promocode';
import { DialogService } from 'primeng/dynamicdialog';
import * as Sentry from '@sentry/angular';
import {
  SubscriptionEffects,
  subscriptionFeatureKey,
  subscriptionReducer,
} from '@store/subscription';
import { UserEffects, userFeatureKey, userReducer } from '@store/user';
import { PromoCodeRepository } from '@core/repositories/promocode.repository';
import { PromoCodeWebRepository } from '@data/repositories/promocode-web.repository';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { authFeatureKey, authReducer } from '@app2/store/auth/auth.reducer';
import { provideCountdown } from 'ngx-countdown';
import { AuthEffects } from '@app2/store/auth/auth.effects';
import { searchFeatureKey, searchReducer } from '@app2/store/search/search.reducer';
import { SearchEffects } from '@app2/store/search/search.effects';
import { AnalystService } from '@app/services/analyst/analyst.service';


export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),

    provideAppInitializer(() => {
      const service = inject(AnalystService);
      return service.applyUserContext();
    }),

    provideStore(),
    provideState({ name: sessionFeatureKey, reducer: sessionReducer }),
    provideState({ name: authFeatureKey, reducer: authReducer }),
    provideState({ name: userFeatureKey, reducer: userReducer }),
    provideState({ name: settingsFeatureKey, reducer: settingsReducer }),
    provideState({ name: promoCodeFeatureKey, reducer: promoCodeReducer }),
    provideState({ name: paymentFeatureKey, reducer: paymentReducer }),
    provideState({ name: subscriptionFeatureKey, reducer: subscriptionReducer }),
    provideState({ name: paymentFeatureKeyV2, reducer: paymentReducerV2 }),
    provideState({ name: searchFeatureKey, reducer: searchReducer }),
    provideEffects([
      InitEffects, FapsEffects, SessionEffects, UserEffects, AuthEffects,
      SettingsEffects, PromoCodeEffects, PaymentEffects, SubscriptionEffects,
      PaymentEffectsV2, SearchEffects,
    ]),
    provideRouterStore(),
    provideStoreDevtools(),
    provideStoreDevtools({ maxAge: 30, logOnly: isDevMode() }),

    VideoDirective,
    InitStore,
    CategoryStore,
    TagStore,
    FapsStore,
    ViewStore,
    AdsStore,

    { provide: AuthRepository, useClass: AuthWebRepository },
    { provide: PromoCodeRepository, useClass: PromoCodeWebRepository },
    { provide: CommonRepository, useClass: CommonWebRepository },
    { provide: AdsRepository, useClass: AdsWebRepository },
    { provide: FapRepository, useClass: FapWebRepository },
    { provide: PornRepository, useClass: PornWebRepository },
    { provide: PaymentRepository, useClass: PaymentWebRepository },
    { provide: UserRepository, useClass: UserWebRepository },
    { provide: MediaRepository, useClass: MediaWebRepository },

    provideI18Next(),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'base, tailwind-base, primeng, theme, tailwind-utilities',
          },
        },
      },
    }),
    DialogService,
    provideCountdown({
      format: 'mm:ss',
      leftTime: 60,
      prettyText: text => {
        return text.split(':')
          .map((v) => `<span class="font-bold">${v}</span>`)
          .join(':');
      },
    }),

    importProvidersFrom(
      JwtModule.forRoot({ config: { tokenGetter: () => getCookie(TOKEN_NAME) } }),
      BackendApiModule.forRoot(() => new Configuration({ basePath: environment.base_url })),
      ToastrModule.forRoot(),
      BrowserAnimationsModule,
    ),
    UserTrackingService,
    UserViewSelectorService,

    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnalytics(() => initializeAnalytics(getApp(), {
      config: { send_page_view: false },
    })),

    I18N_PROVIDERS,

    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
  ],
};
